import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useSmartCaptcha } from '../hooks/useSmartCaptcha';
import '../styles/modal.css';

const INITIAL = { firstName: '', lastName: '', phone: '', email: '', contactPref: 'email', question: '' };

const isValidEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isValidPhone = v => /^[+]?[\d\s\-().]{7,20}$/.test(v.trim());

const QuestionModal = ({ isOpen, onClose, pageTitle }) => {
  const { dark } = useTheme();
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle');
  const [consent, setConsent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const { containerRef: captchaRef, token: captchaToken, reset: resetCaptcha } = useSmartCaptcha(isOpen, dark ? 'dark' : 'light');

  const change = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'email') setEmailError('');
    if (name === 'phone') setPhoneError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.contactPref === 'telegram' && !form.phone.trim()) {
      setPhoneError('Telegram привязан к номеру телефона — укажите его для связи');
      return;
    }
    if (form.phone.trim() && !isValidPhone(form.phone)) {
      setPhoneError('Введите корректный номер (например: +7 900 000 00 00)');
      return;
    }
    if (!form.email.trim()) {
      setEmailError('Email обязателен — он нужен как резервный способ связи');
      return;
    }
    if (!isValidEmail(form.email)) {
      setEmailError('Введите корректный email (например: ivan@example.com)');
      return;
    }
    if (!captchaToken) { setStatus('error'); return; }

    setStatus('sending');
    try {
      const res = await fetch('/api/booking/question', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone,
          email: form.email,
          contactPref: form.contactPref,
          question: form.question,
          pageTitle,
          captchaToken,
        }),
      });
      if (!res.ok) throw new Error('failed');
      // Письмо-подтверждение посетителю отправляет сервер сам (см. /api/booking/question) —
      // отдельный запрос отсюда раньше падал с 401 (у анонимных посетителей нет токена авторизации).
      setStatus('done');
    } catch {
      setStatus('error');
      resetCaptcha();
    }
  };

  const handleClose = () => {
    setForm(INITIAL);
    setStatus('idle');
    setEmailError('');
    setPhoneError('');
    setConsent(false);
    resetCaptcha();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>×</button>

        {status === 'done' ? (
          <div className="modal-success">
            <div className="modal-success-icon">✅</div>
            <h2>Вопрос отправлен!</h2>
            <p>Мы ответим вам в ближайшее время.</p>
            <button className="submit-btn" onClick={handleClose}>Закрыть</button>
          </div>
        ) : (
          <>
            <h2>Задать вопрос</h2>
            {pageTitle && <div className="modal-event-name">{pageTitle}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-row-two">
                <div className="form-group">
                  <label>Имя</label>
                  <input name="firstName" value={form.firstName} onChange={change} placeholder="Иван" />
                </div>
                <div className="form-group">
                  <label>Фамилия</label>
                  <input name="lastName" value={form.lastName} onChange={change} placeholder="Иванов" />
                </div>
              </div>

              <div className="form-group">
                <label>Телефон {form.contactPref === 'telegram' && <em>*</em>}</label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={change}
                  placeholder="+7 900 000 00 00"
                  className={phoneError ? 'input-error' : ''}
                />
                {phoneError && <p className="modal-field-error">{phoneError}</p>}
              </div>

              <div className="form-group">
                <label>Email <em>*</em></label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={change}
                  placeholder="ivan@example.com"
                  className={emailError ? 'input-error' : ''}
                />
                {emailError && <p className="modal-field-error">{emailError}</p>}
              </div>

              <div className="form-group">
                <label>Предпочтительный способ ответа <em>*</em></label>
                <div className="modal-contact-pref">
                  <label className={`modal-pref-option ${form.contactPref === 'email' ? 'selected' : ''}`}>
                    <input type="radio" name="contactPref" value="email" checked={form.contactPref === 'email'} onChange={change} />
                    📧 Email
                  </label>
                  <label className={`modal-pref-option ${form.contactPref === 'telegram' ? 'selected' : ''}`}>
                    <input type="radio" name="contactPref" value="telegram" checked={form.contactPref === 'telegram'} onChange={change} />
                    ✈️ Telegram
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Ваш вопрос <em>*</em></label>
                <textarea
                  name="question"
                  value={form.question}
                  onChange={change}
                  rows={4}
                  placeholder="Напишите ваш вопрос..."
                  required
                />
              </div>

              {status === 'error' && (
                <p className="modal-error">Ошибка отправки. Попробуйте ещё раз.</p>
              )}

              <label className="modal-consent">
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
                <span>
                  Я согласен(а) с{' '}
                  <a href="/personal-data" target="_blank" rel="noopener noreferrer">политикой обработки персональных данных</a>
                </span>
              </label>

              <div ref={captchaRef} className="captcha-wrap" />

              <button type="submit" className="submit-btn" disabled={status === 'sending' || !consent || !captchaToken}>
                {status === 'sending' ? 'Отправляем...' : 'Отправить вопрос'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionModal;
